/**
 * @fileoverview Zcash shared constants and default configuration
 *
 * All endpoint URLs are defaults that can be overridden via ZcashServiceConfig.
 */

import type { ZcashServiceConfig, ZcashNetwork } from './types';

// ---------------------------------------------------------------------------
// Default Service URLs (Winbit32 hosted infrastructure)
// ---------------------------------------------------------------------------

export const DEFAULT_WALLET_SCANNER_URL = 'https://api.zcash.winbit32.com/api';
export const DEFAULT_ZEBRA_RPC_URL = 'https://rpc.zcash.winbit32.com';
export const DEFAULT_LIGHTWALLETD_URL = 'https://lightwalletd.zcash.winbit32.com';
export const DEFAULT_PCZT_BUILDER_URL = 'https://api.zcash.winbit32.com/api';
export const DEFAULT_NETWORK: ZcashNetwork = 'main';

/** Convenience: build a full config from partial overrides */
export function createDefaultConfig(overrides?: Partial<ZcashServiceConfig>): ZcashServiceConfig {
	return {
		walletScannerUrl: overrides?.walletScannerUrl ?? DEFAULT_WALLET_SCANNER_URL,
		zebraRpcUrl: overrides?.zebraRpcUrl ?? DEFAULT_ZEBRA_RPC_URL,
		lightwalletdUrl: overrides?.lightwalletdUrl ?? DEFAULT_LIGHTWALLETD_URL,
		pcztBuilderUrl: overrides?.pcztBuilderUrl ?? DEFAULT_PCZT_BUILDER_URL,
		network: overrides?.network ?? DEFAULT_NETWORK,
		apiKey: overrides?.apiKey,
	};
}

// ---------------------------------------------------------------------------
// Zcash Constants
// ---------------------------------------------------------------------------

/** 1 ZEC = 100,000,000 zatoshis */
export const ZATOSHIS_PER_ZEC = 100_000_000;

/** ZIP-317 base fee (10,000 zat = 0.0001 ZEC) */
export const ZIP317_MARGINAL_FEE = 5_000;
export const ZIP317_GRACE_ACTIONS = 2;
export const ZIP317_P2PKH_STANDARD_INPUT_SIZE = 150;
export const ZIP317_P2PKH_STANDARD_OUTPUT_SIZE = 34;

/** Orchard Merkle tree depth */
export const ORCHARD_MERKLE_DEPTH = 32;

/** Note cache TTL: 5 minutes (ms) */
export const NOTE_CACHE_TTL_MS = 5 * 60 * 1000;

/** Scan job poll interval (ms) */
export const SCAN_POLL_INTERVAL_MS = 3_000;

/** Maximum scan poll attempts */
export const SCAN_MAX_POLL_ATTEMPTS = 120; // 6 minutes at 3s intervals

/** Broadcast timeout (ms) */
export const BROADCAST_TIMEOUT_MS = 120_000;

// ---------------------------------------------------------------------------
// Address Prefixes
// ---------------------------------------------------------------------------

export const ZCASH_PREFIXES = {
	transparent: {
		mainnet: { pubKeyHash: 0x1cb8, scriptHash: 0x1cbd },
		testnet: { pubKeyHash: 0x1d25, scriptHash: 0x1cba },
	},
	shielded: {
		sapling: 'zs1',
		saplingTest: 'ztestsapling',
	},
	unified: 'u1',
} as const;

/** Transparent address version bytes (mainnet) */
export const ZCASH_TRANSPARENT_VERSION = new Uint8Array([0x1c, 0xb8]);

// ---------------------------------------------------------------------------
// bitcoinjs-lib compatible network object for transparent tx building
// ---------------------------------------------------------------------------

export const ZCASH_MAINNET_NETWORK = {
	messagePrefix: '\x18ZCash Signed Message:\n',
	bech32: '',
	bip32: { public: 0x0488b21e, private: 0x0488ade4 },
	pubKeyHash: 0x1cb8,
	scriptHash: 0x1cbd,
	wif: 0x80,
} as const;

export const ZCASH_TESTNET_NETWORK = {
	messagePrefix: '\x18ZCash Signed Message:\n',
	bech32: '',
	bip32: { public: 0x043587cf, private: 0x04358394 },
	pubKeyHash: 0x1d25,
	scriptHash: 0x1cba,
	wif: 0xef,
} as const;

// ---------------------------------------------------------------------------
// Derivation Paths
// ---------------------------------------------------------------------------

/** Standard BIP44 derivation path for Zcash transparent */
export const ZCASH_DERIVATION_PATH = "m/44'/133'/0'/0/0";

/** Sapling derivation (ZIP-32) */
export const ZCASH_SAPLING_PATH = "m/32'/133'/0'";

// ---------------------------------------------------------------------------
// localStorage Keys
// ---------------------------------------------------------------------------

export const STORAGE_KEYS = {
	NOTE_CACHE_PREFIX: 'zcash_note_cache_',
	PENDING_NULLIFIERS_PREFIX: 'zcash_pending_nullifiers_',
	MANUAL_WALLET: 'manual_wallet_connection',
	WINBIT32_MNEMONIC: 'winbit32_zcash_mnemonic',
	WINBIT32_ADDRESSES: 'winbit32_zcash_addresses',
} as const;

/** Standalone localStorage key for the note-cache map */
export const NOTE_CACHE_STORAGE_KEY = 'zcash_shielded_note_cache';

/** Standalone localStorage key for pending nullifiers */
export const PENDING_NULLIFIER_STORAGE_KEY = 'zcash_pending_nullifiers';

/**
 * localStorage key for per-UFVK birthday hints. Once a wallet's first-activity
 * height has been detected we remember it so subsequent (cold-cache) scans can
 * start from there directly, skipping the slow, silent birthday auto-detect
 * phase and unlocking a real progress percentage.
 */
export const BIRTHDAY_HINT_STORAGE_KEY = 'zcash_birthday_hints';

// ---------------------------------------------------------------------------
// WASM Asset Paths
// ---------------------------------------------------------------------------

/** Path to the WASM binary (served from frontend public/) */
export const WASM_KEYS_AND_SEND_PATH = '/webzjs_keys_and_send_bg.wasm?v=ironwood';

